import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { cn } from "utils/cn";
import { INotificationItem } from "api/notifications/notifications.type";
import { resolveNotificationDisplay, getNotificationImageUrl } from "components/WhatsNewMenu/notificationDisplay";

interface ToastCardProps {
  item: INotificationItem;
  exiting: boolean;
  onDismiss: () => void;
  onClick: () => void;
}

// Single card — visual style copied from DownloadButton's "Enhancing resolution" progress popup
// (components/DownloadButton/components/DownloadProgressPopup.tsx: bg-body/border-border card,
// rounded-lg, shadow-lg, h6 title + bodySm subtitle) blended with the bell feed's icon-chip +
// tone-color convention (components/WhatsNewMenu/NotificationItem.tsx).
const ToastCard = ({ item, exiting, onDismiss, onClick }: ToastCardProps) => {
  const { t } = useTranslation();
  const [entered, setEntered] = useState(false);
  // REF-6509: same rule as the bell row — a cover that fails to load renders nothing rather
  // than a broken image box. The toast has NO icon slot (it shows nothing when the backend
  // sends no cover either), so dropping the thumbnail is the existing no-cover appearance, not
  // a new blank state.
  //
  // Keyed on the failed URL rather than a boolean, for the same reason as `NotificationItem`: a
  // coalescing row swaps its cover under the same id, and a boolean would hide every later
  // valid thumbnail.
  const [failedCoverUrl, setFailedCoverUrl] = useState<string | null>(null);
  // REF-6607: a comment mention shows the mentioning teammate's photo (round) in the cover slot.
  const coverUrl = getNotificationImageUrl(item);
  const isAvatar = !item.cover_url && !!coverUrl;
  const coverFailed = !!coverUrl && failedCoverUrl === coverUrl;
  const { title, body } = resolveNotificationDisplay(item, t);

  // Enter transition: mount at translateX/opacity-0, flip to resting state a tick later so the
  // browser actually animates the transition instead of snapping in.
  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));

    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className={cn(
        "flex w-full cursor-pointer items-start gap-3 rounded-lg border border-border bg-body p-3 shadow-lg transition-all duration-300 ease-out",
        entered && !exiting ? "translate-x-0 opacity-100" : "translate-x-4 opacity-0"
      )}
    >
      {coverUrl && !coverFailed && (
        <img src={coverUrl} alt="" onError={() => setFailedCoverUrl(coverUrl)} className={cn("h-10 w-10 flex-shrink-0 object-cover bg-black-200", isAvatar ? "rounded-full" : "rounded")} />
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-foreground text-[13px] font-semibold line-clamp-1">{title}</span>
        {body && <span className="text-muted-foreground text-bodySm custom-line-clamp-2">{body}</span>}
      </div>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
        aria-label={t("common:whats_new.notifications.archive")}
        className="flex-center h-6 w-6 flex-shrink-0 rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" strokeWidth={1.75} />
      </button>
    </div>
  );
};

export default ToastCard;
